import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Bot,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Info,
  MousePointerClick,
  PhoneCall,
  Plus,
  Radio,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Smartphone,
  Table,
  TrendingUp,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge, type BadgeTone } from "../../components/ui/badge";
import { DateRangePicker, presetRange, type DateRangeValue } from "../../components/ui/date-range-picker";
import { Dropdown } from "../../components/ui/dropdown";
import { useToast } from "../../components/ui/toast";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import { whatsappApi, type WhatsAppInstance } from "../admin/whatsapp-api";
import { chatbotApi, type ChatbotAgent } from "../chatbot/chatbot-api";
import { campaignsApi, templatesApi, type WhatsAppTemplate } from "../whatsapp-templates/api";
import { whatsappAnalyticsApi, type WhatsAppAnalytics, type WhatsAppAnalyticsTemplate } from "./api";

const AXIS_TICK = { fill: "#7a736c", fontSize: 12 };
const GRID = "#e6dfd2";

const ymd = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const formatDay = (day: string) => {
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? day
    : parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
};

const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

function formatPhoneNumber(raw?: string | null): string {
  if (!raw) return "No number";
  const cleaned = raw.replace(/\D/g, "");
  if (!cleaned) return raw;
  if (cleaned.length === 12 && cleaned.startsWith("91")) {
    return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  return `+${cleaned}`;
}

function formatTimestamp(value?: string | null) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function connectionStateInfo(status: string) {
  switch (status?.toLowerCase()) {
    case "connected":
      return { tone: "success" as BadgeTone, label: "Connected", pulse: true };
    case "qr_pending":
      return { tone: "warning" as BadgeTone, label: "QR Scan Required", pulse: true };
    case "qr_expired":
      return { tone: "warning" as BadgeTone, label: "QR Expired", pulse: false };
    case "disconnected":
      return { tone: "danger" as BadgeTone, label: "Disconnected", pulse: false };
    case "failed":
      return { tone: "danger" as BadgeTone, label: "Failed", pulse: false };
    default:
      return { tone: "neutral" as BadgeTone, label: status || "Unknown", pulse: false };
  }
}

function integrationBadge(integration: string) {
  if (integration === "WHATSAPP-BUSINESS") {
    return { label: "Meta Cloud API", tone: "violet" as BadgeTone };
  }
  if (integration === "WHATSAPP-BAILEYS") {
    return { label: "Evolution QR", tone: "brand" as BadgeTone };
  }
  return { label: integration || "WhatsApp", tone: "neutral" as BadgeTone };
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-line bg-paper-card p-2.5 shadow-lg dark:bg-[#292524]">
      {label && <p className="mb-1 font-mono text-xs font-semibold text-ink-3">{label}</p>}
      <div className="space-y-1">
        {payload.map((entry: any, i: number) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="h-2 w-2 rounded-full" style={{ background: entry.color || entry.fill }} />
            <span className="text-ink-2">{entry.name}:</span>
            <span className="font-mono font-bold text-ink">
              {typeof entry.value === "number" ? entry.value.toLocaleString("en-IN") : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DailyDeliveryTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const dayData = payload[0]?.payload;
  const totalAttempts = (dayData?.sent || 0) + (dayData?.failed || 0);

  return (
    <div className="rounded-lg border border-line bg-paper-card p-3 shadow-lg dark:bg-[#292524]">
      <div className="flex items-center justify-between gap-4 border-b border-line/50 pb-1.5">
        <span className="font-mono text-xs font-bold text-ink">{label}</span>
        <span className="font-mono text-[11px] font-semibold text-brand">
          {totalAttempts.toLocaleString("en-IN")} total attempts
        </span>
      </div>
      <div className="mt-2 space-y-1.5 text-xs">
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#16a34a]" /> Delivered
          </span>
          <span className="font-mono font-bold text-ink">{(dayData?.delivered || 0).toLocaleString("en-IN")}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#2563eb]" /> Sent (In-Flight)
          </span>
          <span className="font-mono font-bold text-[#2563eb]">{(dayData?.inFlight || 0).toLocaleString("en-IN")}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#ef4444]" /> Failed
          </span>
          <span className="font-mono font-bold text-[#ef4444]">{(dayData?.failed || 0).toLocaleString("en-IN")}</span>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-line/40 pt-1 text-ink-2">
          <span className="inline-flex items-center gap-1.5 text-[#8b5cf6]">
            <span className="h-2 w-2 rounded-full bg-[#8b5cf6]" /> Replies
          </span>
          <span className="font-mono font-bold text-[#8b5cf6]">{(dayData?.replied || 0).toLocaleString("en-IN")}</span>
        </div>
      </div>
    </div>
  );
}

function BroadcastChartTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <div className="rounded-lg border border-line bg-paper-card p-3 shadow-lg dark:bg-[#292524]">
      <div className="flex items-center justify-between gap-3">
        <p className="font-semibold text-ink">{data.fullName}</p>
        {data.dateLabel && <span className="font-mono text-[10px] text-ink-3">{data.dateLabel}</span>}
      </div>
      <p className="text-[11px] text-ink-3">
        Template: <span className="font-medium text-ink-2">{data.template}</span>
      </p>
      <div className="mt-2 space-y-1 border-t border-line/50 pt-1.5 text-xs">
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#16a34a]" /> Sent
          </span>
          <span className="font-mono font-bold text-ink">{data.sent.toLocaleString("en-IN")}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#ef4444]" /> Failed
          </span>
          <span className="font-mono font-bold text-ink">{data.failed.toLocaleString("en-IN")}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#f59e0b]" /> Queued
          </span>
          <span className="font-mono font-bold text-ink">{data.queued.toLocaleString("en-IN")}</span>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-line/40 pt-1 font-semibold text-ink">
          <span>Success Rate</span>
          <span className="font-mono text-[#16a34a]">{data.rate}%</span>
        </div>
      </div>
    </div>
  );
}

function TemplateChartTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const template = payload[0]?.payload;
  if (!template) return null;
  const deliveryPct = template.sent > 0 ? Math.round((template.delivered / template.sent) * 1000) / 10 : 0;
  return (
    <div className="min-w-[220px] rounded-lg border border-line bg-paper-card p-3 shadow-lg dark:bg-[#292524]">
      <p className="font-semibold text-ink">{template.name}</p>
      <p className="font-mono text-[10px] text-ink-3">{template.template_id}</p>
      <div className="mt-2 space-y-1.5 border-t border-line/50 pt-1.5 text-xs">
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#2563eb]" /> Sent
          </span>
          <span className="font-mono font-bold text-ink">{template.sent.toLocaleString("en-IN")}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#16a34a]" /> Delivered
          </span>
          <span className="font-mono font-bold text-[#16a34a]">
            {template.delivered.toLocaleString("en-IN")}{" "}
            <span className="text-[10px] text-ink-3">({deliveryPct}%)</span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#ef4444]" /> Failed
          </span>
          <span className="font-mono font-bold text-[#ef4444]">{template.failed.toLocaleString("en-IN")}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-2">
            <span className="h-2 w-2 rounded-full bg-[#8b5cf6]" /> Replies
          </span>
          <span className="font-mono font-bold text-[#8b5cf6]">{template.replied.toLocaleString("en-IN")}</span>
        </div>
        {template.clicks && template.clicks.length > 0 && (
          <div className="border-t border-line/40 pt-1.5">
            <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-ink-3">Button Clicks</p>
            <div className="space-y-1">
              {template.clicks.map((click: any) => (
                <div key={click.label} className="flex items-center justify-between text-[11px]">
                  <span className="max-w-[140px] truncate text-ink">{click.label}</span>
                  <span className="font-mono font-semibold text-brand">{click.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



export function WhatsAppDashboardPage() {
  const { token, user } = useAuth() as any;
  const clientId = user?.client_id || user?.id;
  const toast = useToast();

  const [range, setRange] = useState<DateRangeValue>(() => ({ ...presetRange("last_7"), preset: "last_7" }));
  const [campaignId, setCampaignId] = useState("");
  const [broadcastChartType, setBroadcastChartType] = useState<"area" | "bar">("area");
  const [dailyChartType, setDailyChartType] = useState<"bar" | "area">("area");
  const [templateView, setTemplateView] = useState<"insights" | "table">("insights");
  const [templateLimit, setTemplateLimit] = useState<5 | 10>(5);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateSortBy, setTemplateSortBy] = useState<"sent" | "delivered" | "rate" | "replied">("sent");
  const [templatePage, setTemplatePage] = useState(1);
  const TEMPLATES_PER_PAGE = 6;
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [agents, setAgents] = useState<ChatbotAgent[]>([]);
  const [instances, setInstances] = useState<WhatsAppInstance[]>([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [data, setData] = useState<WhatsAppAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch campaigns
  const refreshCampaigns = () => {
    setCampaignsLoading(true);
    campaignsApi
      .list()
      .then(setCampaigns)
      .catch(() => setCampaigns([]))
      .finally(() => setCampaignsLoading(false));
  };

  useEffect(() => {
    refreshCampaigns();
  }, []);

  // Fetch templates for friendly name lookup
  useEffect(() => {
    templatesApi
      .list()
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, []);

  // Fetch agents for friendly name lookup
  useEffect(() => {
    if (!token) return;
    chatbotApi
      .agents(token)
      .then(setAgents)
      .catch(() => setAgents([]));
  }, [token]);

  // Fetch WhatsApp numbers / instances with fallback
  useEffect(() => {
    if (!token || !clientId) return;
    setInstancesLoading(true);
    whatsappApi
      .listForClient(token, clientId)
      .then(setInstances)
      .catch(() => {
        chatbotApi
          .instances(token)
          .then((res) => {
            setInstances(
              res.map(
                (i) =>
                  ({
                    instance_name: i.instance_name,
                    phone_number: i.phone_number,
                    status: "connected",
                    integration: "WHATSAPP-BAILEYS",
                    agent_id: null,
                    purposes: ["chatbot"],
                    is_current_for_client: false,
                    business_id: null,
                    app_id: null,
                    created_by: null,
                    created_at: null,
                    updated_at: null,
                    last_status_at: null,
                    instance_id: null,
                    client_id: clientId,
                  } as WhatsAppInstance),
              ),
            );
          })
          .catch(() => setInstances([]));
      })
      .finally(() => setInstancesLoading(false));
  }, [token, clientId]);

  const start = ymd(range.start);
  const end = ymd(range.end);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    whatsappAnalyticsApi
      .summary({ start, end, campaignId: campaignId || undefined })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((cause: Error) => {
        if (!cancelled) {
          setError(cause.message);
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [start, end, campaignId]);

  const templatesMap = useMemo(
    () => new Map(templates.map((t) => [t.template_id, t.name])),
    [templates],
  );

  const agentsMap = useMemo(
    () => new Map(agents.map((a) => [a.id, a.name])),
    [agents],
  );

  const chartData = useMemo(() => {
    return (data?.per_day || []).map((row) => {
      const delivered = row.delivered || 0;
      const sent = row.sent || 0;
      const inFlight = Math.max(0, sent - delivered);
      const failed = row.failed || 0;
      const replied = row.replied || 0;
      const totalAttempts = sent + failed;
      return {
        ...row,
        label: formatDay(row.day),
        delivered,
        inFlight,
        failed,
        replied,
        totalAttempts,
      };
    });
  }, [data]);

  const peakDay = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;
    let peak = chartData[0];
    let max = peak.totalAttempts || 0;
    for (const item of chartData) {
      if ((item.totalAttempts || 0) > max) {
        max = item.totalAttempts || 0;
        peak = item;
      }
    }
    return max > 0 ? { label: peak.label, total: max } : null;
  }, [chartData]);

  const kpis = data?.kpis;

  // Donut chart distribution data for message delivery receipts
  const donutData = useMemo(() => {
    const delivered = kpis?.delivered || 0;
    const sent = kpis?.sent || 0;
    const inFlight = Math.max(0, sent - delivered);
    const failed = kpis?.failed || 0;
    const queued = kpis?.queued || 0;
    const items = [
      { name: "Delivered", value: delivered, color: "#16a34a" },
      { name: "Sent (Awaiting Receipt)", value: inFlight, color: "#2563eb" },
      { name: "Failed", value: failed, color: "#ef4444" },
      { name: "Queued", value: queued, color: "#f59e0b" },
    ].filter((item) => item.value > 0);
    return items;
  }, [kpis]);

  // Top recent broadcast performance for full-width graphical comparison
  const campaignComparisonData = useMemo(() => {
    // Reverse slice so the timeline flows chronologically from earlier (left) to latest (right)
    const recent = [...(campaigns || []).slice(0, 10)].reverse();
    return recent.map((c) => {
      const total = c.total_recipients || 0;
      const sent = c.sent_count || 0;
      const failed = c.failed_count || 0;
      const queued = c.queued_count || Math.max(0, total - sent - failed);
      const rate = total > 0 ? Math.round((sent / total) * 100) : (sent > 0 ? 100 : 0);
      const name = c.campaign || c.campaign_id || "Campaign";
      const shortName = name.length > 12 ? `${name.slice(0, 10)}…` : name;
      const templateName =
        c.template_id && templatesMap.get(c.template_id)
          ? templatesMap.get(c.template_id)
          : c.template_id || (c.message_kind === "ai_agent" ? "AI Opener" : "Template");

      let dateLabel = "";
      const rawDate = c.created_at || c.scheduled_at;
      if (rawDate) {
        const d = new Date(rawDate);
        if (!Number.isNaN(d.getTime())) {
          dateLabel = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        }
      }

      return {
        fullName: name,
        shortName,
        dateLabel,
        template: templateName,
        sent,
        failed,
        queued,
        total,
        rate,
      };
    });
  }, [campaigns, templatesMap]);

  const allTemplates = useMemo(() => {
    return (data?.templates || []).map((t) => {
      const delivered = t.delivered || 0;
      const sent = t.sent || 0;
      const failed = t.failed || 0;
      const replied = t.replied || 0;
      const inFlight = Math.max(0, sent - delivered - failed);
      const rate = pct(delivered, sent);
      const totalClicks = (t.clicks || []).reduce((acc, c) => acc + c.count, 0);
      return {
        ...t,
        sent,
        delivered,
        failed,
        replied,
        inFlight,
        rate,
        totalClicks,
      };
    });
  }, [data]);

  const filteredTemplates = useMemo(() => {
    let list = [...allTemplates];
    if (templateSearch.trim()) {
      const q = templateSearch.toLowerCase().trim();
      list = list.filter(
        (t) => t.name.toLowerCase().includes(q) || t.template_id.toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      if (templateSortBy === "delivered") return b.delivered - a.delivered || b.sent - a.sent;
      if (templateSortBy === "rate") return b.rate - a.rate || b.sent - a.sent;
      if (templateSortBy === "replied") return b.replied - a.replied || b.sent - a.sent;
      return b.sent - a.sent || b.delivered - a.delivered;
    });
    return list;
  }, [allTemplates, templateSearch, templateSortBy]);

  const topTemplates = useMemo(() => {
    return filteredTemplates.slice(0, templateLimit);
  }, [filteredTemplates, templateLimit]);

  const topTemplatesChartData = useMemo(() => {
    return topTemplates.map((t) => ({
      ...t,
      displayName: t.name.length > 18 ? `${t.name.slice(0, 16)}…` : t.name,
    }));
  }, [topTemplates]);

  const aggregatedClicks = useMemo(() => {
    const clickMap = new Map<string, { label: string; count: number; templates: string[] }>();
    let totalClicks = 0;
    for (const t of allTemplates) {
      for (const c of t.clicks || []) {
        totalClicks += c.count;
        const existing = clickMap.get(c.label);
        if (existing) {
          existing.count += c.count;
          if (!existing.templates.includes(t.name)) existing.templates.push(t.name);
        } else {
          clickMap.set(c.label, { label: c.label, count: c.count, templates: [t.name] });
        }
      }
    }
    const buttons = Array.from(clickMap.values()).sort((a, b) => b.count - a.count);
    return { buttons, totalClicks };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates]);

  const templateSummary = useMemo(() => {
    const totalSent = allTemplates.reduce((sum, t) => sum + t.sent, 0);
    const totalDelivered = allTemplates.reduce((sum, t) => sum + t.delivered, 0);
    const totalFailed = allTemplates.reduce((sum, t) => sum + t.failed, 0);
    const totalReplied = allTemplates.reduce((sum, t) => sum + t.replied, 0);
    const avgDeliveryRate = totalSent > 0 ? pct(totalDelivered, totalSent) : 0;
    const topPerformer =
      allTemplates.length > 0 ? [...allTemplates].sort((a, b) => b.sent - a.sent)[0] : null;

    return {
      totalSent,
      totalDelivered,
      totalFailed,
      totalReplied,
      avgDeliveryRate,
      topPerformer,
      count: allTemplates.length,
    };
  }, [allTemplates]);

  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / TEMPLATES_PER_PAGE));
  const safeTemplatePage = Math.min(templatePage, totalPages);
  const paginatedTemplates = useMemo(() => {
    const startIdx = (safeTemplatePage - 1) * TEMPLATES_PER_PAGE;
    return filteredTemplates.slice(startIdx, startIdx + TEMPLATES_PER_PAGE);
  }, [filteredTemplates, safeTemplatePage]);

  const exportCsv = () => {
    if (!data) return;
    const lines: string[][] = [
      ["WhatsApp overview", `${data.window.start} to ${data.window.end}`],
      ["Metric", "Value"],
      ["Total attempts", String(data.kpis.total)],
      ["Sent", String(data.kpis.sent)],
      ["Delivered", String(data.kpis.delivered)],
      ["Failed", String(data.kpis.failed)],
      ["Queued", String(data.kpis.queued)],
      ["Replies", String(data.kpis.replied)],
      ["Unique contacts replied", String(data.kpis.unique_replied)],
      ["Unique contacts reached", String(data.kpis.unique_contacts)],
      ["Response rate %", String(data.kpis.response_rate)],
      [],
      ["Day", "Sent", "Delivered", "Failed", "Replies"],
      ...data.per_day.map((row) => [row.day, String(row.sent), String(row.delivered), String(row.failed), String(row.replied)]),
      [],
      ["Template", "Sent", "Delivered", "Failed", "Replies", "Button", "Clicks"],
    ];
    for (const template of data.templates) {
      if (template.clicks.length === 0) {
        lines.push([template.name, String(template.sent), String(template.delivered), String(template.failed), String(template.replied), "", ""]);
      }
      for (const click of template.clicks) {
        lines.push([template.name, String(template.sent), String(template.delivered), String(template.failed), String(template.replied), click.label, String(click.count)]);
      }
    }
    const csv = lines.map((row) => row.map((cell) => `"${(cell || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const filename = `whatsapp-overview-${data.window.start}-${data.window.end}.csv`;
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success("Export downloaded", filename);
  };

  return (
    <div className="space-y-6 px-4 sm:px-5 md:px-6 pb-8 pt-3 sm:pt-4">
      {/* ── Top Header & Global Filter Controls ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-sm font-semibold text-ink">WhatsApp Overview</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-48 shrink-0">
            <Dropdown
              ariaLabel="Select campaign"
              value={campaignId}
              onChange={setCampaignId}
              searchable
              options={[
                { value: "", label: "All campaigns" },
                ...campaigns.map((c) => ({
                  value: c.campaign_id,
                  label: c.campaign || c.campaign_id,
                })),
              ]}
            />
          </div>
          <DateRangePicker value={range} onChange={setRange} align="right" />
          <button
            type="button"
            onClick={() => {
              setCampaignId("");
              setRange({ ...presetRange("last_7"), preset: "last_7" });
              setTemplateSearch("");
              setTemplatePage(1);
            }}
            disabled={campaignId === "" && range.preset === "last_7" && !templateSearch}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg border border-line bg-paper px-3 py-2 text-xs font-semibold text-ink-2 shadow-2xs transition hover:bg-soft hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed dark:bg-[#1f1c1a]",
              (campaignId !== "" || range.preset !== "last_7" || templateSearch) &&
                "border-brand/40 text-brand hover:border-brand hover:text-brand-hover",
            )}
            title="Reset filters to default (All campaigns, Last 7 days)"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>Reset</span>
          </button>
          <button
            onClick={exportCsv}
            disabled={!data}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-4 py-3 text-sm text-[#b91c1c] dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
          Could not load WhatsApp analytics: {error}
        </div>
      )}

      {/* ── 1. High-Level KPI Summary Cards ── */}
      <div className={cn("grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5", loading && "opacity-50")}>
        <Stat
          label="Messages Sent"
          value={fmt(kpis?.sent)}
          subtitle={`${fmt(kpis?.total)} attempts\n${fmt(kpis?.queued)} still queued`}
        />
        <Stat
          label="Delivered"
          value={fmt(kpis?.delivered)}
          subtitle={`${pct(kpis?.delivered || 0, kpis?.sent || 0)}% of sent`}
          tone="success"
        />
        <Stat
          label="Failed"
          value={fmt(kpis?.failed)}
          subtitle={`${pct(kpis?.failed || 0, kpis?.total || 0)}% of attempts`}
          tone="danger"
        />
        <Stat
          label="Replies"
          value={fmt(kpis?.unique_replied)}
          subtitle={`${fmt(kpis?.replied)} reply messages\n${fmt(kpis?.unique_contacts)} contacts reached`}
        />
        <Stat
          label="Response Rate"
          value={`${kpis?.response_rate ?? 0}%`}
          subtitle="Contacts who replied / contacts reached"
        />
      </div>

      {/* ── 4. Graphs Section: Delivery per Day & Outcome Distribution ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        {/* Left: Delivery per Day Composed / Stacked Chart (8 cols) */}
        <div className="flex flex-col justify-between rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark sm:p-5 lg:col-span-8">
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-ink">Daily Delivery Volume</h2>
                {peakDay && (
                  <span className="rounded-md bg-soft px-2 py-0.5 font-mono text-[11px] font-semibold text-brand">
                    Peak: {peakDay.label} ({peakDay.total} msgs)
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-4 text-xs">
                <div className="flex items-center gap-3 text-ink-3">
                  <Legend color="#16a34a" label="Delivered" />
                  <Legend color="#2563eb" label="Sent / In-Flight" />
                  <Legend color="#ef4444" label="Failed" />
                  <Legend color="#8b5cf6" label="Replies" />
                </div>
                <div className="flex items-center rounded-lg border border-line bg-soft/60 p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setDailyChartType("area")}
                    className={cn(
                      "rounded-md px-2 py-0.5 font-medium transition-colors",
                      dailyChartType === "area"
                        ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                        : "text-ink-3 hover:text-ink",
                    )}
                  >
                    Area Trend
                  </button>
                  <button
                    type="button"
                    onClick={() => setDailyChartType("bar")}
                    className={cn(
                      "rounded-md px-2 py-0.5 font-medium transition-colors",
                      dailyChartType === "bar"
                        ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                        : "text-ink-3 hover:text-ink",
                    )}
                  >
                    Stacked Bars
                  </button>
                </div>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={260}>
              {dailyChartType === "bar" ? (
                <BarChart data={chartData} margin={{ top: 12, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke={GRID} vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<DailyDeliveryTooltip />} cursor={{ fill: "#f0eee6" }} />
                  <Bar dataKey="delivered" name="Delivered" stackId="day" fill="#16a34a" radius={[0, 0, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="inFlight" name="Sent (Awaiting Receipt)" stackId="day" fill="#2563eb" radius={[0, 0, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="failed" name="Failed" stackId="day" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  <Line type="monotone" dataKey="replied" name="Replies" stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 3, fill: "#8b5cf6" }} activeDot={{ r: 5 }} />
                </BarChart>
              ) : (
                <AreaChart data={chartData} margin={{ top: 12, right: 12, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="dailySentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="dailyDeliveredGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#16a34a" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#16a34a" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="dailyFailedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={GRID} vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<DailyDeliveryTooltip />} />
                  <Area type="monotone" dataKey="inFlight" name="Sent (Awaiting Receipt)" stroke="#2563eb" strokeWidth={2.5} fill="url(#dailySentGrad)" dot={{ r: 3.5, stroke: "#2563eb", strokeWidth: 1.5, fill: "#fff" }} />
                  <Area type="monotone" dataKey="delivered" name="Delivered" stroke="#16a34a" strokeWidth={2} fill="url(#dailyDeliveredGrad)" dot={{ r: 3, stroke: "#16a34a", strokeWidth: 1.5, fill: "#fff" }} />
                  <Area type="monotone" dataKey="failed" name="Failed" stroke="#ef4444" strokeWidth={2} fill="url(#dailyFailedGrad)" dot={{ r: 3, stroke: "#ef4444", strokeWidth: 1.5, fill: "#fff" }} />
                  <Line type="monotone" dataKey="replied" name="Replies" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3, fill: "#8b5cf6" }} />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex items-center gap-1.5 border-t border-line/40 pt-2 text-[11px] text-ink-3">
            <Info className="h-3.5 w-3.5 shrink-0 text-ink-3" />
            <span>Delivered counts come from WhatsApp delivery receipts and start at receipt tracking activation.</span>
          </div>
        </div>

        {/* Right: Delivery Outcome Donut Distribution (4 cols) */}
        <div className="flex flex-col justify-between rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark sm:p-5 lg:col-span-4">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink">Delivery Breakdown</h2>
              <Badge tone={(kpis?.delivered || 0) > 0 ? "success" : "neutral"} className={(kpis?.delivered || 0) > 0 ? "" : "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"}>
                {(kpis?.delivered || 0) > 0
                  ? `${pct(kpis?.delivered || 0, kpis?.sent || 0)}% Delivered`
                  : `${pct(kpis?.sent || 0, kpis?.total || 0)}% Dispatched`}
              </Badge>
            </div>

            <div className="relative mt-2">
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  {/* Subtle gauge background track */}
                  <Pie
                    data={[{ value: 1 }]}
                    dataKey="value"
                    innerRadius={58}
                    outerRadius={80}
                    fill="#f4f1eb"
                    strokeWidth={0}
                    isAnimationActive={false}
                  />
                  <Pie
                    data={donutData.length > 0 ? donutData : [{ name: "No data", value: 1, color: "#d6d3d1" }]}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={58}
                    outerRadius={80}
                    paddingAngle={donutData.length > 1 ? 3 : 0}
                    strokeWidth={0}
                    cornerRadius={4}
                  >
                    {(donutData.length > 0 ? donutData : [{ name: "No data", value: 1, color: "#d6d3d1" }]).map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-mono text-2xl font-bold tracking-tight text-ink">
                  {fmt(kpis?.total)}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                  Attempts
                </span>
                <span className="mt-0.5 font-mono text-[11px] font-medium text-[#2563eb]">
                  {fmt(kpis?.sent)} sent
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-2.5 border-t border-line/60 pt-3.5 text-xs">
            {/* Delivered Progress Row */}
            <div>
              <div className="flex items-center justify-between text-ink-2">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <span className="h-2 w-2 rounded-full bg-[#16a34a]" /> Delivered
                </span>
                <span className="font-mono text-ink">
                  <span className="font-semibold">{fmt(kpis?.delivered)}</span>
                  <span className="ml-1 text-[11px] text-ink-3">({pct(kpis?.delivered || 0, kpis?.total || 0)}%)</span>
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-soft">
                <div
                  className="h-full rounded-full bg-[#16a34a] transition-all duration-500"
                  style={{ width: `${pct(kpis?.delivered || 0, kpis?.total || 0)}%` }}
                />
              </div>
            </div>

            {/* Sent (Awaiting Receipt) Progress Row */}
            <div>
              <div className="flex items-center justify-between text-ink-2">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <span className="h-2 w-2 rounded-full bg-[#2563eb]" /> Sent (Awaiting Receipt)
                </span>
                <span className="font-mono text-ink">
                  <span className="font-semibold">{fmt(Math.max(0, (kpis?.sent || 0) - (kpis?.delivered || 0)))}</span>
                  <span className="ml-1 text-[11px] text-ink-3">({pct(Math.max(0, (kpis?.sent || 0) - (kpis?.delivered || 0)), kpis?.total || 0)}%)</span>
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-soft">
                <div
                  className="h-full rounded-full bg-[#2563eb] transition-all duration-500"
                  style={{ width: `${pct(Math.max(0, (kpis?.sent || 0) - (kpis?.delivered || 0)), kpis?.total || 0)}%` }}
                />
              </div>
            </div>

            {/* Failed Progress Row */}
            <div>
              <div className="flex items-center justify-between text-ink-2">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <span className="h-2 w-2 rounded-full bg-[#ef4444]" /> Failed
                </span>
                <span className="font-mono text-ink">
                  <span className="font-semibold text-[#ef4444]">{fmt(kpis?.failed)}</span>
                  <span className="ml-1 text-[11px] text-ink-3">({pct(kpis?.failed || 0, kpis?.total || 0)}%)</span>
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-soft">
                <div
                  className="h-full rounded-full bg-[#ef4444] transition-all duration-500"
                  style={{ width: `${pct(kpis?.failed || 0, kpis?.total || 0)}%` }}
                />
              </div>
            </div>

            {/* Queued Progress Row (if any) */}
            {(kpis?.queued || 0) > 0 && (
              <div>
                <div className="flex items-center justify-between text-ink-2">
                  <span className="inline-flex items-center gap-1.5 font-medium">
                    <span className="h-2 w-2 rounded-full bg-[#f59e0b]" /> Queued
                  </span>
                  <span className="font-mono text-ink">
                    <span className="font-semibold">{fmt(kpis?.queued)}</span>
                    <span className="ml-1 text-[11px] text-ink-3">({pct(kpis?.queued || 0, kpis?.total || 0)}%)</span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-soft">
                  <div
                    className="h-full rounded-full bg-[#f59e0b] transition-all duration-500"
                    style={{ width: `${pct(kpis?.queued || 0, kpis?.total || 0)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── 5. Recent Broadcasts & Real-Time Delivery Feed ── */}
      <div className="rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-brand" />
            <h2 className="text-sm font-semibold text-ink">Recent Broadcasts</h2>
            <Badge tone="neutral">{campaigns.length} campaigns</Badge>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={refreshCampaigns}
              title="Refresh campaigns"
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-elevated px-2.5 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-soft"
            >
              <RefreshCw className={cn("h-3.5 w-3.5 text-ink-3", campaignsLoading && "animate-spin text-brand")} />
              Refresh
            </button>
            <Link
              to="/whatsapp/campaigns"
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand transition-colors hover:text-brand-hover hover:underline"
            >
              View all campaigns <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-line p-8 text-center">
            <Send className="h-8 w-8 text-ink-3" />
            <p className="mt-2 text-sm font-medium text-ink">No campaigns dispatched yet</p>
            <p className="mt-1 text-xs text-ink-3">
              Schedule your first broadcast campaign to send templates to your contact list.
            </p>
            <Link
              to="/whatsapp/campaigns"
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover"
            >
              <Send className="h-3.5 w-3.5" /> Launch Campaign
            </Link>
          </div>
        ) : (
          <div className="rounded-lg border border-line/50 bg-elevated p-3.5 sm:p-4 dark:border-stone-800">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs font-semibold text-ink">
                Broadcast Volume Trajectory (Latest {campaignComparisonData.length} Campaigns)
              </span>
              <div className="flex flex-wrap items-center gap-4 text-xs">
                <div className="flex items-center gap-3 text-ink-3">
                  <Legend color="#16a34a" label="Sent" />
                  <Legend color="#ef4444" label="Failed" />
                  <Legend color="#f59e0b" label="Queued" />
                </div>
                <div className="flex items-center rounded-lg border border-line bg-soft/60 p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setBroadcastChartType("area")}
                    className={cn(
                      "rounded-md px-2 py-0.5 font-medium transition-colors",
                      broadcastChartType === "area"
                        ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                        : "text-ink-3 hover:text-ink",
                    )}
                  >
                    Smooth Area
                  </button>
                  <button
                    type="button"
                    onClick={() => setBroadcastChartType("bar")}
                    className={cn(
                      "rounded-md px-2 py-0.5 font-medium transition-colors",
                      broadcastChartType === "bar"
                        ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                        : "text-ink-3 hover:text-ink",
                    )}
                  >
                    Bar Columns
                  </button>
                </div>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={230}>
              {broadcastChartType === "area" ? (
                <AreaChart data={campaignComparisonData} margin={{ top: 12, right: 16, left: -20, bottom: 4 }}>
                  <defs>
                    <linearGradient id="broadcastSentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#16a34a" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#16a34a" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="broadcastFailedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="broadcastQueuedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={GRID} vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="shortName"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<BroadcastChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="sent"
                    name="Sent"
                    stroke="#16a34a"
                    strokeWidth={2.5}
                    fill="url(#broadcastSentGrad)"
                    dot={{ r: 4, stroke: "#16a34a", strokeWidth: 2, fill: "#ffffff" }}
                    activeDot={{ r: 6, stroke: "#16a34a", strokeWidth: 2.5, fill: "#ffffff" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="failed"
                    name="Failed"
                    stroke="#ef4444"
                    strokeWidth={2}
                    fill="url(#broadcastFailedGrad)"
                    dot={{ r: 3, stroke: "#ef4444", strokeWidth: 1.5, fill: "#ffffff" }}
                    activeDot={{ r: 5, stroke: "#ef4444", strokeWidth: 2, fill: "#ffffff" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="queued"
                    name="Queued"
                    stroke="#f59e0b"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    fill="url(#broadcastQueuedGrad)"
                    dot={{ r: 3, stroke: "#f59e0b", strokeWidth: 1.5, fill: "#ffffff" }}
                    activeDot={{ r: 5, stroke: "#f59e0b", strokeWidth: 2, fill: "#ffffff" }}
                  />
                </AreaChart>
              ) : (
                <BarChart data={campaignComparisonData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis
                    dataKey="shortName"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<BroadcastChartTooltip />} cursor={{ fill: "#f0eee6" }} />
                  <Bar dataKey="sent" name="Sent" fill="#16a34a" radius={[3, 3, 0, 0]} maxBarSize={32} />
                  <Bar dataKey="failed" name="Failed" fill="#ef4444" radius={[3, 3, 0, 0]} maxBarSize={32} />
                  <Bar dataKey="queued" name="Queued" fill="#f59e0b" radius={[3, 3, 0, 0]} maxBarSize={32} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ── 6. Template Performance Section ── */}
      <div className="rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark sm:p-5">
        {/* Section Header with Controls */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-line/60 pb-3.5">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <h2 className="text-sm font-semibold text-ink">Template Performance</h2>
            <Badge tone="neutral">{data?.templates.length ?? 0} templates</Badge>
            {templateSummary.totalSent > 0 && (
              <span className="hidden font-mono text-xs text-ink-3 sm:inline">
                {templateSummary.totalSent.toLocaleString("en-IN")} total sends • {templateSummary.avgDeliveryRate}% delivered
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Search Input */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
              <input
                type="text"
                placeholder="Search templates…"
                value={templateSearch}
                onChange={(e) => {
                  setTemplateSearch(e.target.value);
                  setTemplatePage(1);
                }}
                className="h-8 w-32 rounded-lg border border-line bg-paper pl-8 pr-2.5 text-xs text-ink outline-none transition placeholder:text-ink-3 focus:border-brand dark:bg-[#1f1c1a] sm:w-44"
              />
            </div>

            {/* View Switcher: Insights vs Table */}
            <div className="flex items-center rounded-lg border border-line bg-soft/50 p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setTemplateView("insights")}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition",
                  templateView === "insights"
                    ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                    : "text-ink-3 hover:text-ink",
                )}
              >
                <BarChart3 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Insights</span>
              </button>
              <button
                type="button"
                onClick={() => setTemplateView("table")}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition",
                  templateView === "table"
                    ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                    : "text-ink-3 hover:text-ink",
                )}
              >
                <Table className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>

            {/* Jump to Template Studio */}
            <Link
              to="/whatsapp/templates"
              className="inline-flex items-center gap-1 rounded-lg border border-line bg-paper-card px-2.5 py-1 text-xs font-medium text-ink hover:border-brand dark:bg-[#292524]"
              title="Manage and create templates in Template Studio"
            >
              <ExternalLink className="h-3.5 w-3.5 text-ink-3" />
              <span className="hidden md:inline">Studio</span>
            </Link>
          </div>
        </div>

        {loading ? (
          <p className="py-12 text-center text-sm text-ink-3">Loading templates…</p>
        ) : !data || data.templates.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-3">No template sends in this window.</p>
        ) : templateView === "insights" ? (
          /* ── Graphical Insights View ── */
          <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-12">
            {/* Left: Top Templates Volume & Delivery Bar Chart */}
            <div className="flex flex-col lg:col-span-8">
              <div className="flex flex-1 flex-col justify-between rounded-xl border border-line/50 bg-paper/60 p-4 dark:bg-[#1f1c1a]/60">
                <div>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-ink">Volume & Delivery Comparison</span>
                      <span className="text-[11px] text-ink-3">
                        (Top {topTemplates.length} of {filteredTemplates.length})
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <div className="w-36 shrink-0">
                        <Dropdown
                          ariaLabel="Sort templates"
                          size="sm"
                          value={templateSortBy}
                          onChange={(v) => {
                            setTemplateSortBy(v as any);
                            setTemplatePage(1);
                          }}
                          options={[
                            { value: "sent", label: "Sort: Most Sent" },
                            { value: "delivered", label: "Sort: Most Delivered" },
                            { value: "rate", label: "Sort: Delivery Rate %" },
                            { value: "replied", label: "Sort: Most Replies" },
                          ]}
                        />
                      </div>

                      <div className="flex items-center rounded-md border border-line bg-soft/50 p-0.5 text-[11px]">
                        <button
                          type="button"
                          onClick={() => setTemplateLimit(5)}
                          className={cn(
                            "rounded px-2 py-0.5 font-medium transition",
                            templateLimit === 5
                              ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                              : "text-ink-3 hover:text-ink",
                          )}
                        >
                          Top 5
                        </button>
                        <button
                          type="button"
                          onClick={() => setTemplateLimit(10)}
                          className={cn(
                            "rounded px-2 py-0.5 font-medium transition",
                            templateLimit === 10
                              ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                              : "text-ink-3 hover:text-ink",
                          )}
                        >
                          Top 10
                        </button>
                      </div>
                    </div>
                  </div>

                  {topTemplatesChartData.length === 0 ? (
                    <div className="flex h-[320px] items-center justify-center">
                      <p className="text-xs text-ink-3">No templates match your search filter.</p>
                    </div>
                  ) : (
                    <div className="h-[320px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          layout="vertical"
                          data={topTemplatesChartData}
                          margin={{ top: 8, right: 20, left: 10, bottom: 0 }}
                          barCategoryGap={templateLimit === 5 ? "26%" : "12%"}
                        >
                          <CartesianGrid stroke={GRID} horizontal={false} />
                          <XAxis
                            type="number"
                            tick={AXIS_TICK}
                            axisLine={false}
                            tickLine={false}
                            allowDecimals={false}
                          />
                          <YAxis
                            type="category"
                            dataKey="displayName"
                            tick={{ ...AXIS_TICK, fontSize: templateLimit === 5 ? 12 : 11 }}
                            axisLine={false}
                            tickLine={false}
                            width={125}
                          />
                          <Tooltip content={<TemplateChartTooltip />} cursor={{ fill: "#f0eee6", opacity: 0.5 }} />
                          <Bar
                            dataKey="delivered"
                            name="Delivered"
                            stackId="tmpl"
                            fill="#16a34a"
                            radius={[0, 0, 0, 0]}
                            maxBarSize={templateLimit === 5 ? 20 : 13}
                          />
                          <Bar
                            dataKey="inFlight"
                            name="Sent (Pending)"
                            stackId="tmpl"
                            fill="#2563eb"
                            radius={[0, 0, 0, 0]}
                            maxBarSize={templateLimit === 5 ? 20 : 13}
                          />
                          <Bar
                            dataKey="failed"
                            name="Failed"
                            stackId="tmpl"
                            fill="#ef4444"
                            radius={[0, 3, 3, 0]}
                            maxBarSize={templateLimit === 5 ? 20 : 13}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-line/40 pt-2 text-xs text-ink-2">
                  <div className="flex flex-wrap items-center gap-4">
                    <Legend color="#16a34a" label="Delivered" />
                    <Legend color="#2563eb" label="Sent / In-Flight" />
                    <Legend color="#ef4444" label="Failed" />
                  </div>
                  {filteredTemplates.length > topTemplates.length && (
                    <button
                      type="button"
                      onClick={() => setTemplateView("table")}
                      className="text-xs font-medium text-brand hover:underline"
                    >
                      View all {filteredTemplates.length} templates in table →
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right: CTA Button Click Activity + Health Highlights + Quick Actions */}
            <div className="flex flex-col gap-4 lg:col-span-4">
              {/* Button Click Activity Card */}
              <div className="flex flex-1 flex-col justify-between rounded-xl border border-line/60 bg-paper/60 p-4 dark:bg-[#1f1c1a]/60">
                <div>
                  <div className="flex items-center justify-between border-b border-line/60 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-soft text-brand">
                        <MousePointerClick className="h-4 w-4" />
                      </div>
                      <h3 className="text-xs font-semibold text-ink">Button Clicks & CTAs</h3>
                    </div>
                    <Badge tone={aggregatedClicks.totalClicks > 0 ? "brand" : "neutral"}>
                      {aggregatedClicks.totalClicks.toLocaleString("en-IN")} clicks
                    </Badge>
                  </div>

                  {aggregatedClicks.buttons.length === 0 ? (
                    <div className="flex flex-col items-center justify-center px-2 py-7 text-center">
                      <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-soft text-ink-3">
                        <MousePointerClick className="h-4 w-4 opacity-50" />
                      </div>
                      <p className="mt-2 text-xs font-medium text-ink">No Button Clicks Recorded</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-ink-3">
                        Interactions with quick-reply buttons and CTAs will rank here automatically.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {aggregatedClicks.buttons.slice(0, 4).map((btn) => {
                        const share =
                          aggregatedClicks.totalClicks > 0
                            ? Math.round((btn.count / aggregatedClicks.totalClicks) * 100)
                            : 0;
                        return (
                          <div key={btn.label} className="rounded-lg bg-soft/50 p-2 text-xs">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate font-medium text-ink" title={btn.label}>
                                {btn.label}
                              </span>
                              <span className="shrink-0 font-mono font-bold text-brand">
                                {btn.count}{" "}
                                <span className="font-sans text-[10px] font-normal text-ink-3">({share}%)</span>
                              </span>
                            </div>
                            <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-line/50">
                              <div
                                className="h-full rounded-full bg-brand transition-all"
                                style={{ width: `${share}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Template Health Highlights */}
              <div className="flex flex-1 flex-col justify-between rounded-xl border border-line/60 bg-paper/60 p-4 dark:bg-[#1f1c1a]/60">
                <div>
                  <div className="flex items-center gap-2 border-b border-line/60 pb-3">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-[#16a34a] dark:bg-emerald-950/40">
                      <TrendingUp className="h-4 w-4" />
                    </div>
                    <h3 className="text-xs font-semibold text-ink">Template Health Summary</h3>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg bg-soft/40 p-2.5">
                      <p className="font-mono text-[10px] uppercase text-ink-3">Total Sends</p>
                      <p className="mt-1 font-mono text-base font-bold text-ink">
                        {templateSummary.totalSent.toLocaleString("en-IN")}
                      </p>
                    </div>
                    <div className="rounded-lg bg-soft/40 p-2.5">
                      <p className="font-mono text-[10px] uppercase text-ink-3">Avg Delivery</p>
                      <p className="mt-1 font-mono text-base font-bold text-[#16a34a]">
                        {templateSummary.avgDeliveryRate}%
                      </p>
                    </div>
                    <div className="col-span-2 rounded-lg bg-soft/40 p-2.5">
                      <p className="font-mono text-[10px] uppercase text-ink-3">Most Active Template</p>
                      <p className="mt-0.5 truncate text-xs font-semibold text-ink">
                        {templateSummary.topPerformer ? templateSummary.topPerformer.name : "None"}
                      </p>
                      <p className="font-mono text-[10px] text-ink-3">
                        {templateSummary.topPerformer
                          ? `${templateSummary.topPerformer.sent.toLocaleString("en-IN")} messages sent`
                          : "No sends in this window"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Action Link */}
                <div className="mt-3 flex items-center justify-between border-t border-line/50 pt-2.5">
                  <span className="text-[11px] text-ink-3">Need to edit or build templates?</span>
                  <Link
                    to="/whatsapp/templates"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                  >
                    Template Studio <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ── Searchable & Paginated Table View (Prevents Infinite Scrolling) ── */
          <div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-wider text-ink-3">
                    <th className="pb-2.5 pr-3 font-semibold">Template</th>
                    <th className="pb-2.5 pr-3 text-right font-semibold">Sent</th>
                    <th className="pb-2.5 pr-3 text-right font-semibold">Delivered</th>
                    <th className="pb-2.5 pr-3 text-right font-semibold">Failed</th>
                    <th className="pb-2.5 pr-3 text-right font-semibold">Replies</th>
                    <th className="pb-2.5 font-semibold">Button clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTemplates.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-xs text-ink-3">
                        No templates found matching &ldquo;{templateSearch}&rdquo;
                      </td>
                    </tr>
                  ) : (
                    paginatedTemplates.map((template) => (
                      <tr key={template.template_id} className="border-b border-line/60 last:border-0 hover:bg-soft/40">
                        <td className="py-3 pr-3">
                          <p className="font-medium text-ink">{template.name}</p>
                          <p className="font-mono text-[10px] text-ink-3">{template.template_id}</p>
                        </td>
                        <td className="py-3 pr-3 text-right font-mono text-ink">
                          {template.sent.toLocaleString("en-IN")}
                        </td>
                        <td className="py-3 pr-3 text-right font-mono text-[#16a34a]">
                          {template.delivered.toLocaleString("en-IN")}
                          <span className="ml-1 text-[10px] text-ink-3">{template.rate}%</span>
                        </td>
                        <td className="py-3 pr-3 text-right font-mono text-[#ef4444]">
                          {template.failed.toLocaleString("en-IN")}
                        </td>
                        <td className="py-3 pr-3 text-right font-mono text-ink">
                          {template.replied.toLocaleString("en-IN")}
                        </td>
                        <td className="py-3">
                          <ButtonClicks template={template} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line/60 pt-3 text-xs">
              <span className="text-ink-3">
                Showing{" "}
                <span className="font-mono font-medium text-ink">
                  {filteredTemplates.length > 0 ? (safeTemplatePage - 1) * TEMPLATES_PER_PAGE + 1 : 0}
                </span>{" "}
                to{" "}
                <span className="font-mono font-medium text-ink">
                  {Math.min(safeTemplatePage * TEMPLATES_PER_PAGE, filteredTemplates.length)}
                </span>{" "}
                of <span className="font-mono font-medium text-ink">{filteredTemplates.length}</span> templates
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={safeTemplatePage <= 1}
                  onClick={() => setTemplatePage((p) => Math.max(1, p - 1))}
                  className="flex items-center gap-1 rounded-md border border-line bg-paper px-2.5 py-1 text-xs text-ink transition hover:bg-soft/50 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-[#1f1c1a]"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Previous
                </button>
                <span className="font-mono text-xs text-ink-3">
                  {safeTemplatePage} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={safeTemplatePage >= totalPages}
                  onClick={() => setTemplatePage((p) => Math.min(totalPages, p + 1))}
                  className="flex items-center gap-1 rounded-md border border-line bg-paper px-2.5 py-1 text-xs text-ink transition hover:bg-soft/50 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-[#1f1c1a]"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function ButtonClicks({ template }: { template: WhatsAppAnalyticsTemplate }) {
  if (template.clicks.length === 0) {
    return <span className="text-xs text-ink-3">No clicks</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {template.clicks.map((click) => (
        <span
          key={click.label}
          className="inline-flex items-center gap-1 rounded-md bg-brand-soft px-2 py-1 text-xs text-brand"
        >
          <MousePointerClick className="h-3 w-3" />
          <span className="max-w-[180px] truncate" title={click.label}>
            {click.label}
          </span>
          <span className="font-mono font-semibold">{click.count}</span>
        </span>
      ))}
    </div>
  );
}

function Stat({
  label,
  value,
  subtitle,
  tone,
}: {
  label: string;
  value: string;
  subtitle?: string;
  tone?: "success" | "danger";
}) {
  return (
    <div className="rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">{label}</p>
      <p
        className={cn(
          "mt-2 font-mono text-2xl font-bold text-ink",
          tone === "success" && "text-[#16a34a]",
          tone === "danger" && "text-[#ef4444]",
        )}
      >
        {value}
      </p>
      {subtitle && <p className="mt-1 whitespace-pre-line text-xs text-ink-3">{subtitle}</p>}
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

const fmt = (value?: number) => (value ?? 0).toLocaleString("en-IN");
